// Shared API client for the owner dashboard.
//
// All owner endpoints sit behind the same CloudFront distribution that serves
// this SPA, so requests use relative URLs (same-origin) and rely on the session
// cookie. On a 401 the owner is no longer authenticated — bounce to login.
//
// Used by every view (Today / Guests / Calendar / Your salon). Views should
// catch the thrown error and degrade gracefully (loading / empty / error
// states): several read-layer endpoints are built-as-code but not yet deployed,
// so a 404 must never crash a view.

const LOGIN_PATH = '/account/login';

class ApiError extends Error {
  constructor(message, status, code = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Read a browser cookie value. `csrf_token` is set by the session handler on
 * login (session.py) and is the source of the double-submit CSRF token.
 */
function getCookie(name) {
  const m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
  return m ? decodeURIComponent(m[1]) : '';
}

/**
 * Double-submit CSRF header. The backend rejects every mutating request
 * (POST/PUT/PATCH/DELETE) with 403 csrf_failed unless X-CSRF-Token equals the
 * csrf_token cookie. Single source of the header for the whole dashboard — any
 * POST that skips it gets a 403; CloudFront preserves the API error status.
 */
export function csrfHeaders() {
  const token = getCookie('csrf_token');
  return token ? { 'X-CSRF-Token': token } : {};
}

/**
 * Fetch JSON from an owner endpoint.
 *
 * @param {string} path - e.g. '/v1/owner/dashboard'
 * @param {object} [options] - fetch options (method, headers, body...)
 * @returns {Promise<any>} parsed JSON body
 * @throws {ApiError} on non-2xx (except 401, which redirects to login)
 */
export async function apiFetch(path, options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const mutating = method !== 'GET' && method !== 'HEAD';
  let response;
  try {
    response = await fetch(path, {
      credentials: 'include',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(mutating ? csrfHeaders() : {}),
        ...(options.headers || {}),
      },
    });
  } catch (networkErr) {
    // Connection-level failure (offline, DNS, CORS) — surface as an ApiError
    // with no status so views can show a generic error state.
    throw new ApiError(networkErr.message || 'Network request failed', 0);
  }

  if (response.status === 401) {
    // Session expired or never authenticated — leave the SPA for login.
    window.location.href = LOGIN_PATH;
    // Return a never-resolving promise so callers don't briefly render with
    // empty data before the navigation completes.
    return new Promise(() => {});
  }

  if (!response.ok) {
    let details = null;
    try { details = await response.json(); } catch { /* Non-JSON errors retain a safe generic message. */ }
    throw new ApiError(
      typeof details?.message === 'string' ? details.message : `Request failed (${response.status})`,
      response.status,
      typeof details?.error === 'string' ? details.error : null,
    );
  }

  // Some endpoints may legitimately return an empty body.
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

/**
 * Sign the owner out: clear the server-side SESSION cookie, then leave the SPA
 * for the login page. Canonical logout for the whole dashboard — the avatar
 * menu (MobileTopBar) and the "Your salon" account row both call this so the
 * sign-out behaviour lives in exactly one place. The redirect runs even if the
 * POST fails (offline / already-expired) so the owner always lands on login.
 */
export async function logout() {
  try {
    const { clearOwnerTurnStorage } = await import('./ownerTurns.js');
    clearOwnerTurnStorage();
  } catch { /* Recovery cleanup must not prevent signing out. */ }
  try {
    await fetch('/account/logout', {
      method: 'POST',
      credentials: 'include',
      headers: csrfHeaders(),
    });
  } catch (e) {
    // Best-effort: redirect regardless so a flaky network can't trap the owner.
  }
  window.location.href = LOGIN_PATH;
}

export { ApiError, LOGIN_PATH };
